import { memo } from 'react';

import { FeedbackSentiment } from '@/types/appointmentAttendanceFeedback';
import { ChartingStatus } from '@/types/appointments';
import { Pill, Tooltip } from '@/ui';
import AppointmentAttendanceStatus from '@/ui/components/AppointmentAttendanceStatus';
import DualTime from '@/ui/components/DualTime';
import ScheduledBySourceBadge from '@/ui/components/ScheduledBySourceBadge';
import { CHARTING_STATUS_LABELS, resolveChartingStatus } from '@/utils/chartingStatus';
import { getDrugScreenMeta } from '@/utils/drugScreenMeta';
import formatStaffName from '@/utils/formatStaffName';
import snakeCaseToTitleCase from '@/utils/snakeToTitleCase';

import type { Props } from './types';

const chartingPillColor: Record<ChartingStatus, 'primary' | 'destructive' | 'warning'> = {
  [ChartingStatus.SESSION_COMPLETE]: 'primary',
  [ChartingStatus.SESSION_INCOMPLETE]: 'destructive',
  [ChartingStatus.CHART_PENDING]: 'warning'
};

/**
 * NOTE: This component is business/domain-specific and does not belong in the
 * generic UI primitives library (ui/components). It should be moved to a more
 * appropriate domain-specific location in the codebase.
 */
const AppointmentAttendancePills = ({
  action,
  onClick,
  status,
  cancellationReason,
  appointmentTypeName,
  dateTime,
  memberTZ,
  provider,
  appointment,
  scheduledBySource,
  groupEnrollment,
  rescheduledBySource,
  autoCancelEligible,
  chartingCompleted,
  chartingStatus,
  drugScreen,
  feedback
}: Props) => {
  const Wrapper = onClick ? 'button' : 'div';
  const resolvedChartingStatus = resolveChartingStatus(chartingStatus, chartingCompleted);

  return (
    <Wrapper
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      className={`inline-flex flex-wrap items-center gap-1.5 ${onClick ? ' cursor-pointer' : ''}`}>
      {status && <AppointmentAttendanceStatus status={status} reason={cancellationReason} />}

      {appointmentTypeName && (
        <Pill color="secondary" size="sm">
          {appointmentTypeName}
        </Pill>
      )}

      {provider && (
        <Pill color="neutral" size="sm">
          {formatStaffName({ staff: provider })}
        </Pill>
      )}

      {dateTime && (
        <Pill color="neutral" size="sm">
          <DualTime memberTZ={memberTZ ?? undefined} dateTime={dateTime} showWeekday />
        </Pill>
      )}

      <ScheduledBySourceBadge
        scheduledBySource={appointment?.scheduled_by_source ?? scheduledBySource}
        initiator={appointment?.initiator}
        groupEnrollment={groupEnrollment}
      />

      {rescheduledBySource && (
        <Tooltip content="How this appointment was rescheduled">
          <Pill color="warning" size="sm">
            {rescheduledBySource}
          </Pill>
        </Tooltip>
      )}

      {autoCancelEligible && (
        <Tooltip content="This appointment is eligible for automatic cancellation">
          <Pill color="warning" size="sm">
            Auto-Cancel Enabled
          </Pill>
        </Tooltip>
      )}

      <Pill color={chartingPillColor[resolvedChartingStatus]} size="sm">
        {CHARTING_STATUS_LABELS[resolvedChartingStatus]}
      </Pill>

      {drugScreen &&
        (() => {
          const { color, emoji } = getDrugScreenMeta(drugScreen);
          const drugColorMap: Record<string, 'primary' | 'destructive' | 'warning' | 'neutral'> = {
            green: 'primary',
            red: 'destructive',
            orange: 'warning',
            yellow: 'warning'
          };
          return (
            <Pill color={drugColorMap[color] ?? 'neutral'} size="sm">
              {`${snakeCaseToTitleCase(drugScreen.kind)} ${emoji} ${snakeCaseToTitleCase(drugScreen.result)}`}
            </Pill>
          );
        })()}

      {feedback &&
        feedback.length > 0 &&
        (() => {
          const isPositive = feedback[0].sentiment === FeedbackSentiment.POSITIVE;
          return (
            <Pill color={isPositive ? 'primary' : 'destructive'} size="sm">
              {isPositive ? 'Positive Feedback' : 'Negative Feedback'}
            </Pill>
          );
        })()}

      {action && (
        <span className="ml-1 border-l border-gray-200 pl-1.5 flex items-center self-stretch">
          {action}
        </span>
      )}
    </Wrapper>
  );
};

export default memo(AppointmentAttendancePills);
